import { Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react'

import { Button, CheckboxField, SelectField, StatusMessage, TextField } from '@krp/ui-kit'

import type { ProfileEditorVm, ProfilesActions, ProfilesVm } from '../model/types'
import { QueryState } from '../../pricing/ui/QueryState'
import '../../pricing/ui/onec-ui.css'

function ErrorBox({ title, error }: { title: string; error: { message: string; code: string; correlationId: string | null } }) {
  return (
    <StatusMessage
      kind="error"
      title={title}
      text={error.message}
      meta={`Код: ${error.code}${error.correlationId === null ? '' : ' · запрос ' + error.correlationId}`}
    />
  )
}

function Editor({ editor, actions }: { editor: ProfileEditorVm; actions: ProfilesActions }) {
  const { draft } = editor
  return (
    <section className="onec-section" aria-labelledby="onec-profile-editor">
      <h3 id="onec-profile-editor">{draft.id === null ? 'Новый профиль колонок' : 'Профиль колонок'}</h3>
      <div className="onec-row">
        <TextField label="Имя профиля" value={draft.name} onChange={(name) => actions.editorChanged({ name })} />
        <TextField label="Поставщик" value={draft.supplierName} onChange={(supplierName) => actions.editorChanged({ supplierName })} />
      </div>
      <div className="onec-row">
        <TextField label="Лист (подсказка)" value={draft.sheetNameHint} onChange={(sheetNameHint) => actions.editorChanged({ sheetNameHint })} />
        <TextField label="Строка шапки (0 — нет)" value={draft.headerRowIndex} onChange={(headerRowIndex) => actions.editorChanged({ headerRowIndex })} />
        <TextField label="Единица по умолчанию" value={draft.defaultUnit} onChange={(defaultUnit) => actions.editorChanged({ defaultUnit })} />
        <TextField label="Ставка НДС по умолчанию" value={draft.defaultVatRate} onChange={(defaultVatRate) => actions.editorChanged({ defaultVatRate })} />
      </div>
      <div className="onec-row">
        <CheckboxField label="Цена с НДС" checked={draft.priceIncludesVat} onChange={(priceIncludesVat) => actions.editorChanged({ priceIncludesVat })} />
        <CheckboxField label="Количество в упаковках" checked={draft.quantityInPackages} onChange={(quantityInPackages) => actions.editorChanged({ quantityInPackages })} />
      </div>

      <h4>Колонки</h4>
      {draft.columns.map((column, index) => (
        <div className="onec-row" key={index}>
          <SelectField
            label={`Роль колонки ${String(index + 1)}`}
            value={column.role}
            options={editor.roleOptions}
            onChange={(role) => {
              const option = editor.roleOptions.find((candidate) => candidate.value === role)
              if (option !== undefined) actions.editorColumnChanged(index, { role: option.value })
            }}
          />
          <TextField label={`Заголовок ${String(index + 1)}`} value={column.headerText} onChange={(headerText) => actions.editorColumnChanged(index, { headerText })} />
          <TextField label={`Номер колонки ${String(index + 1)}`} value={column.columnIndex} onChange={(columnIndex) => actions.editorColumnChanged(index, { columnIndex })} />
          <Button variant="ghost" aria-label={`Убрать колонку ${String(index + 1)}`} onClick={() => actions.editorColumnRemoved(index)} icon={<Trash2 size={16} aria-hidden="true" />}>
            Убрать
          </Button>
        </div>
      ))}
      <div className="onec-row">
        <Button icon={<Plus size={16} aria-hidden="true" />} onClick={actions.editorColumnAdded}>
          Добавить колонку
        </Button>
      </div>

      {editor.problem !== null && <p className="onec-problem">{editor.problem}</p>}
      {editor.unknownOutcome && (
        <StatusMessage
          kind="unavailable"
          title="Неизвестно, сохранён ли профиль"
          text="Ответ не получен. Повтор безопасен: он уйдёт с теми же ключами, и дубль не создастся."
        />
      )}
      {editor.error !== null && !editor.unknownOutcome && <ErrorBox title="Профиль не сохранён" error={editor.error} />}
      <div className="onec-row">
        <Button variant="primary" busy={editor.busy} disabled={!editor.canSave} onClick={actions.editorSaved}>
          {editor.unknownOutcome ? 'Повторить сохранение' : 'Сохранить профиль'}
        </Button>
        <Button variant="ghost" onClick={actions.editorClosed}>
          Отмена
        </Button>
      </div>
    </section>
  )
}

/** Окно «Профили колонок импорта» базы 1С: список, создание, правка, удаление. */
export function ImportProfilesScreen({ vm, actions }: { vm: ProfilesVm; actions: ProfilesActions }) {
  return (
    <section className="onec-screen" aria-labelledby="onec-profiles-title">
      <header className="onec-section__head">
        <h2 id="onec-profiles-title">Профили колонок импорта</h2>
        <div className="onec-row">
          <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload}>
            Обновить
          </Button>
          <Button variant="primary" icon={<Plus size={16} aria-hidden="true" />} onClick={actions.createStarted}>
            Новый профиль
          </Button>
        </div>
      </header>

      <div className="onec-row" role="search">
        <TextField label="Поставщик" type="search" value={vm.filter.supplier} onChange={(supplier) => actions.filterChanged({ supplier })} />
        <CheckboxField label="Показывать удалённые" checked={vm.filter.includeInactive} onChange={(includeInactive) => actions.filterChanged({ includeInactive })} />
      </div>

      {vm.editor !== null && <Editor editor={vm.editor} actions={actions} />}
      {vm.removal.error !== null && <ErrorBox title="Профиль не удалён" error={vm.removal.error} />}

      <QueryState query={vm.query} emptyText="Профилей пока нет" onRetry={actions.reload} />
      {vm.query.status === 'ready' && (
        <div className="onec-scroll">
          <table className="onec-table">
            <thead>
              <tr>
                <th>Имя</th>
                <th>Поставщик</th>
                <th>Колонки</th>
                <th>Применение</th>
                <th>Изменён</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {vm.rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    {row.name} {!row.isActive && <em>(удалён)</em>}
                  </td>
                  <td>{row.supplier}</td>
                  <td>{row.columnsText}</td>
                  <td>{row.usageText}</td>
                  <td>{row.updatedText}</td>
                  <td>
                    <div className="onec-row">
                      <Button variant="ghost" aria-label={`Изменить профиль ${row.name}`} icon={<Pencil size={16} aria-hidden="true" />} onClick={() => actions.editStarted(row.id)}>
                        Изменить
                      </Button>
                      {row.isActive && (
                        <Button variant="ghost" aria-label={`Удалить профиль ${row.name}`} busy={vm.removal.busy} icon={<Trash2 size={16} aria-hidden="true" />} onClick={() => actions.deleted(row.id)}>
                          Удалить
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
